import React, { useState, useEffect } from "react";
import { PackageOpen, LayoutDashboard, Wallet, ListOrdered, BookOpen, LogOut, Ship, Warehouse, ClipboardList, Receipt, ChevronDown, Lock, CreditCard, Shield, Coins, History, FileText, FileUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { section: "Warehouse", items: [
    { id: "warehouse", label: "Inventory", icon: Warehouse, feature: "warehouse" },
    { id: "shipments", label: "FBA Shipments", icon: Ship, feature: "shipments" },
    { id: "po", label: "Purchase Orders", icon: ClipboardList, feature: "po" },
  ] },
  { section: "Sales Dashboard", items: [
    { id: "overview", label: "Overview", icon: LayoutDashboard, feature: "overview" },
    { id: "orders", label: "Order Details", icon: ListOrdered, feature: "orders" },
    { id: "cost", label: "Cost & Profit", icon: Wallet, feature: "cost" },
    { id: "history", label: "Product History", icon: History, feature: "overview" },
  ] },
  { section: "Accounts", items: [
    { id: "transactions", label: "Transactions", icon: Receipt, feature: "tx",
      children: [{ id: "tx-us", label: "Amazon US" }, { id: "tx-eu", label: "Amazon EU" }] },
    { id: "othercosts", label: "Other Costs", icon: Coins, feature: "cost" },
  ] },
  { section: "More", items: [
    { id: "register", label: "Upload Register", icon: FileUp, feature: "overview" },
    { id: "guide", label: "Reports Guide", icon: BookOpen, feature: "guide" },
    { id: "billing", label: "Plans & Billing", icon: CreditCard },
  ] },
];

const FLAT_NAV = NAV.flatMap((s) => s.items).flatMap((n) => (n.children ? n.children.map((c) => ({ ...c, icon: n.icon })) : [n]));

function NavButton({ item, active, onChange, locked }) {
  const Icon = item.icon;
  const on = active === item.id;
  return (
    <button
      data-testid={`nav-${item.id}`}
      onClick={() => (locked ? onChange("billing") : onChange(item.id))}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
        on ? "bg-[#007AFF]/15 text-white border border-[#007AFF]/40"
           : locked ? "text-[#5a5a5a] hover:text-[#A3A3A3] hover:bg-white/5 border border-transparent"
           : "text-[#A3A3A3] hover:text-white hover:bg-white/5 border border-transparent"
      }`}
    >
      {Icon && <Icon className={`h-4 w-4 ${on ? "text-[#007AFF]" : ""}`} />}
      <span className="flex-1 text-left">{item.label}</span>
      {locked && <Lock className="h-3.5 w-3.5 opacity-70" data-testid={`lock-${item.id}`} />}
    </button>
  );
}

function NavGroup({ item, active, onChange }) {
  const Icon = item.icon;
  const childActive = item.children.some((c) => c.id === active);
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  return (
    <div>
      <button
        data-testid={`nav-${item.id}`}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
          childActive ? "text-white" : "text-[#A3A3A3] hover:text-white hover:bg-white/5"
        } border border-transparent`}
      >
        {Icon && <Icon className={`h-4 w-4 ${childActive ? "text-[#007AFF]" : ""}`} />}
        {item.label}
        <ChevronDown className={`h-4 w-4 ml-auto opacity-60 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 ml-4 pl-3 border-l border-[#1a1a1a] space-y-1">
          {item.children.map((c) => {
            const on = active === c.id;
            return (
              <button
                key={c.id}
                data-testid={`nav-${c.id}`}
                onClick={() => onChange(c.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  on ? "bg-[#007AFF]/15 text-white border border-[#007AFF]/40"
                     : "text-[#A3A3A3] hover:text-white hover:bg-white/5 border border-transparent"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ active, onChange }) {
  const { user, logout, features, role } = useAuth();
  const allowed = new Set(features || []);
  const isLocked = (item) => item.feature && !allowed.has(item.feature) && role !== "admin";
  const sections = NAV.map((s) => ({ ...s, items: [...s.items] }));
  if (role === "admin") sections[sections.length - 1].items.push(
    { id: "admin", label: "Admin", icon: Shield },
    { id: "pricing", label: "Pricing", icon: Coins },
    { id: "cms", label: "Website (CMS)", icon: FileText },
  );

  return (
    <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-[#1a1a1a] bg-[#080808] h-screen sticky top-0">
      <div className="flex items-center gap-2 px-6 h-16 border-b border-[#1a1a1a]">
        <PackageOpen className="h-6 w-6 text-[#007AFF]" />
        <span className="font-head font-extrabold text-lg tracking-tight text-white">FBAStar</span>
      </div>
      <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
        {sections.map((sec) => (
          <div key={sec.section} data-testid={`nav-section-${sec.section}`}>
            <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-widest text-[#555]">{sec.section}</p>
            <div className="space-y-1">
              {sec.items.map((item) =>
                item.children
                  ? (isLocked(item)
                      ? <NavButton key={item.id} item={item} active={active} onChange={onChange} locked />
                      : <NavGroup key={item.id} item={item} active={active} onChange={onChange} />)
                  : <NavButton key={item.id} item={item} active={active} onChange={onChange} locked={isLocked(item)} />
              )}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-[#1a1a1a]">
        <a
          href="/learn"
          target="_blank"
          rel="noreferrer"
          data-testid="help-center-link"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#A3A3A3] hover:text-white hover:bg-white/5 transition-colors duration-200"
        >
          <BookOpen className="h-4 w-4" /> Help Center
        </a>
        <div className="px-3 py-2 mb-1">
          <p className="text-sm font-semibold text-white truncate">{user?.name || "Seller"}</p>
          <p className="text-xs text-[#666] truncate">{user?.email}</p>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#A3A3A3] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors duration-200"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}

export { NAV, FLAT_NAV };
